/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.client.model

import io.ktor.http.HttpStatusCode
import systems.frictionless.impulse.rest.model.ServerError

/**
 * A response from the REST web service.
 *
 * Note that this represents a non-streaming response, so the entire response is loaded before returning.
 */
sealed class RestResponse<T> {
    /**
     * The request was successful and the client was able to parse the response.
     */
    data class Success<T>(val result: T) : RestResponse<T>()

    sealed class Failure<T> : RestResponse<T>() {
        /**
         * The client was not able to communicate with the server.  Examples could include the server is down or
         * the client has no Internet connection.  The solution is usually to check the client connection and retry
         * momentarily.
         *
         * If the client has a connection but the server is down, then retrying a significant time later is recommended.
         */
        data class Connection<T>(val throwable: Throwable) : Failure<T>()

        // 1. We probably want more information, such as an error message string or object from the server.
        // 2. We may not necessarily want to leak the ktor API, but it is convenient for us to print out the name
        // of the response code in addition to the number

        /**
         * The server did respond and provided the given HTTP response code.
         */
        data class Server<T>(val httpStatusCode: HttpStatusCode, val serverError: ServerError) : Failure<T>()

        /**
         * The failure happened on the client, such as JSON parsing failed.
         */
        data class Client<T>(val throwable: Throwable?) : Failure<T>()
    }

    /**
     * @param remapper Maps a successful response to a different type.
     */
    fun <U> remap(remapper: (success: RestResponse.Success<T>) -> RestResponse<U>): RestResponse<U> {
        return when (this) {
            is Success -> {
                remapper(this)
            }
            is Failure -> {
                // Kotlin compiler bug requires suppression
                @Suppress("USELESS_CAST")
                when (val failure = this as Failure) {
                    is Failure.Connection -> {
                        Failure.Connection(failure.throwable)
                    }
                    is Failure.Client -> {
                        Failure.Client(failure.throwable)
                    }
                    is Failure.Server -> {
                        Failure.Server(failure.httpStatusCode, failure.serverError)
                    }
                }
            }
        }
    }
}
